import React, { createContext, useContext, useState, useEffect } from 'react';

const DarkModeContext = createContext();

export const DarkModeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    // Check if there's a saved preference in localStorage
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      return JSON.parse(savedMode);
    }
    // If no saved preference, check system preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));

    const body = document.body;
    const html = document.documentElement;

    if (darkMode) {
      body.classList.add('dark-mode');
      html.classList.add('dark');
    } else {
      body.classList.remove('dark-mode');
      html.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prevMode => !prevMode);
  };

  const enableDarkMode = () => {
    setDarkMode(true);
  };

  const disableDarkMode = () => {
    setDarkMode(false);
  };

  const isDarkModePreferred = () => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  // Removed automatic system preference listener to allow manual toggle control
  // Users can manually toggle dark mode and it will persist in localStorage

  return (
    <DarkModeContext.Provider value={{ 
      darkMode, 
      toggleDarkMode, 
      enableDarkMode, 
      disableDarkMode,
      isDarkModePreferred 
    }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};
